'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import posthog from 'posthog-js';

import { track } from '@repo/analytics';

import { keypointIdFromPathname } from '@/lib/video-href';
import { MAIN_SCROLL_CONTAINER_ID } from '@/components/app-shell/app-shell';

/**
 * Client-side analytics tracker mounted from the analysis pages
 * (`/articles/[slug]` and `/videos/youtube/[slug]`).
 *
 * **CRE-793 (2026-06-12):** ported to @repo/analytics `track()` (doctrine §6),
 * aligned trigger to tracking-plan-v2 §4 (≥30% scroll OR ≥2 keypoint
 * interactions, no dwell gate), and additionally fires `article_consumed`
 * on articles so the web variant matches the iframe variant's event-name
 * surface. Closes the gap where 793 web article visitors fired 0
 * `article_consumed` events in 90d.
 *
 * Fires up to three events per mount:
 *
 * 1. **`analysis_consumed`** — once per session per (content_type × slug)
 *    when EITHER ≥30% scroll OR ≥2 keypoint interactions are observed.
 *    The §2 conversion-funnel downstream stage from `analysis_opened` —
 *    the wedge between "saw the page" and "got value from the page."
 *
 * 2. **`article_consumed`** — additionally fires when `contentType === 'article'`
 *    (mirrors the iframe event), so the cross-surface comparison works
 *    natively. Carries `article_id` (= slug) + `read_pct` (scroll-derived) +
 *    `surface_detail: 'analysis_page'` (origin comes from the boot `surface`
 *    super-prop — never passed per-event, §2c). Deduped on the same
 *    (content_type × slug) key as `analysis_consumed`.
 *
 * 3. **`keypoint_link_received`** — fires when:
 *    - `kpId` is non-null (the URL had `?kp=<id>`), AND
 *    - `$initial_referring_domain` is NOT skim.plus or extension.skim.plus
 *
 *    The AHA #2 measurement — every fire is one successful share-recipient
 *    landing. Fires immediately on mount (no dwell gating) because the
 *    arrival itself IS the activation moment for this funnel.
 *
 * Idempotency: sessionStorage keys per (event × content_type × slug). A
 * re-render or re-mount of the same page within the same session does not
 * double-fire. Cross-session firing is intentional — same article tomorrow
 * = new consumption event.
 *
 * Keypoint interactions: the tracker listens for `skim:keypoint-interacted`
 * window CustomEvents (keypoint interaction handlers call the exported
 * `dispatchKeypointInteracted()` helper). When the count reaches 2, fires
 * the `keypoint_2plus` trigger. Per-variant trigger reachability:
 *   - articles: scroll leg only (no keypoint UI on web articles today)
 *   - videos: keypoint leg only — the analysis renders in an overlay
 *     modal, so the page scroll container behind it belongs to the feed
 *     and a feed scroll must not count as consumption
 * The spec's OR semantics allows either leg alone.
 */

interface AnalysisConsumedTrackerProps {
  contentType: 'article' | 'video';
  slug: string;
  keypointsCount?: number;
  /**
   * Count keypoint interactions only while the URL is still this page's own
   * path (`/videos/youtube/{slug}`, or its `/keypoint/{id}` share path).
   * For a page that stays mounted under the `@modal` intercept: the v2 video
   * page (CRE-960 P1a) has no inline modal, so a soft navigation to another
   * video opens that video's modal over it, and the modal's interactions must
   * not count toward this page's `analysis_consumed`. Off by default (every
   * other mount is unchanged).
   */
  onlyOnOwnPath?: boolean;
  /**
   * The element the analysis scrolls in, for the article scroll leg: the
   * article analysis modal's scroller. `null` while it has not mounted (the
   * scroll leg waits for it). Omitted, `#main-scroll-container` is used.
   */
  scrollContainer?: HTMLElement | null;
}

/** Window CustomEvent name the tracker counts keypoint interactions on. */
export const KEYPOINT_INTERACTED_EVENT = 'skim:keypoint-interacted';

/**
 * Fire-and-forget notifier for keypoint interaction handlers (keypoint
 * select/play, accordion expand, timeline marker click). Keeps the event
 * name in ONE place so dispatchers and the listener can't drift.
 * Safe to call from any handler — no-ops outside the browser.
 */
export const dispatchKeypointInteracted = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(KEYPOINT_INTERACTED_EVENT));
  } catch {
    // Analytics must never break the interaction path.
  }
};

const SESSION_KEY = (type: string, slug: string) =>
  `posthog:analysis_consumed:${type}:${slug}`;

const ARTICLE_SESSION_KEY = (slug: string) =>
  `posthog:article_consumed:${slug}`;

const KP_SESSION_KEY = (type: string, slug: string, kp: string) =>
  `posthog:keypoint_link_received:${type}:${slug}:${kp}`;

const SCROLL_THRESHOLD_PCT = 0.3; // §4 spec
const KEYPOINT_INTERACTIONS_THRESHOLD = 2; // §4 spec

export const AnalysisConsumedTracker: React.FC<
  AnalysisConsumedTrackerProps
> = ({
  contentType,
  slug,
  keypointsCount,
  onlyOnOwnPath = false,
  scrollContainer,
}) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  // A shared keypoint landing carries the keypoint in the PATH
  // (/videos/youtube/<slug>/keypoint/<id>); legacy shares + in-session
  // playback use `?kp=`. The path form is stable across in-modal navigation
  // (the desktop URL-sync leaves the keypoint path as landed), so it cleanly
  // marks a genuine deep-link entry for keypoint_link_received below.
  const kpId = keypointIdFromPathname(pathname) ?? searchParams.get('kp');
  const scrolledPastRef = useRef(false);
  const keypointInteractionsRef = useRef(0);
  const firedRef = useRef(false);
  const lastScrollPctRef = useRef(0);
  // The `kp` present when this tracker first mounted. A genuine share
  // recipient hard-loads `/videos/youtube/<slug>?kp=<id>`, so kp is set at
  // mount. In-session playback instead ADDS/CHANGES `?kp=` via the modal's
  // router.replace on every keypoint advance, which re-ran this effect and
  // logged internal navigation as viral landings (measured 2026-07-04: 35 of
  // 41 fires were mid-session, avg ~7 min in). Only the entry kp counts.
  const entryKpRef = useRef(kpId);

  // ----- AHA #2: keypoint_link_received -----
  // Fires once, only when the analysis view was ENTERED via a `?kp=`
  // deep-link from off-domain (a share recipient landed). We do NOT gate on
  // dwell because the arrival itself is the moment we want to measure.
  useEffect(() => {
    if (!kpId) return;
    // Not a deep-link entry (kp arrived later via in-session playback), or a
    // subsequent keypoint after the landing one -> not a viral landing.
    if (!entryKpRef.current || kpId !== entryKpRef.current) return;
    if (typeof window === 'undefined') return;

    const key = KP_SESSION_KEY(contentType, slug, kpId);
    if (sessionStorage.getItem(key)) return;

    // Pull the *initial* referring domain via PostHog session properties
    // if available; fall back to document.referrer.
    let referrer: string | null = null;
    try {
      referrer =
        (posthog.get_property?.('$initial_referring_domain') as
          | string
          | null
          | undefined) ?? null;
    } catch {
      referrer = null;
    }
    if (!referrer && typeof document !== 'undefined') {
      try {
        const r = document.referrer;
        referrer = r ? new URL(r).hostname : null;
      } catch {
        referrer = null;
      }
    }

    const fromOffDomain =
      !referrer ||
      (referrer !== 'skim.plus' &&
        referrer !== 'extension.skim.plus' &&
        !referrer.endsWith('.skim.plus'));

    if (fromOffDomain) {
      track('keypoint_link_received', {
        kp_id: kpId,
        referrer: referrer ?? '(direct)',
      });
      sessionStorage.setItem(key, '1');
    }
  }, [contentType, slug, kpId]);

  // ----- ACTIVATION: analysis_consumed + article_consumed -----
  // Trigger: ≥30% scroll OR ≥2 keypoint interactions.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const sessionKey = SESSION_KEY(contentType, slug);
    if (sessionStorage.getItem(sessionKey)) {
      firedRef.current = true;
      return;
    }

    // Resolve the real scroll surface. The app-shell renders the content
    // inside `#main-scroll-container` (the outer shell is `h-svh` +
    // `overflow-hidden`, so `window` NEVER scrolls on the analysis routes).
    // Listening on `window` here left the scroll leg DEAD on articles — the
    // only reachable trigger for web articles (no keypoint UI). Fall back to
    // window when the container is absent (hard load before shell mount, or a
    // future non-shell surface). CRE-836.
    //
    // VIDEO variant: the analysis renders inside an overlay modal, and
    // `#main-scroll-container` belongs to the background feed BEHIND it —
    // scrolling the feed under the modal is not consumption. The keypoint-
    // interaction leg (now dispatched by the modal's keypoint handlers) is
    // the reachable trigger for videos, which the §4 OR semantics allows,
    // so the scroll leg is gated to articles only.
    //
    // ARTICLE variant in the modal: `scrollContainer` is the modal's own
    // scroller, which is what the reader scrolls; the feed behind is
    // scroll-locked.
    const scrollLegEnabled =
      contentType === 'article' && scrollContainer !== null;
    const scrollEl =
      scrollContainer ??
      document.getElementById(MAIN_SCROLL_CONTAINER_ID) ??
      null;

    const fire = (trigger: 'scroll_30pct' | 'keypoint_2plus') => {
      if (firedRef.current) return;
      // Re-check the session key at fire time: the tracker can be mounted
      // twice for the same (content_type × slug) — the canonical slug page
      // mounts one instance and the analysis modal mounts another — and
      // both listeners hear the same window event. First one wins.
      if (sessionStorage.getItem(sessionKey)) {
        firedRef.current = true;
        return;
      }
      firedRef.current = true;
      const readPct = Math.min(100, Math.round(lastScrollPctRef.current * 100));

      // 1. Canonical §4 event — fires on both articles + videos.
      track('analysis_consumed', {
        content_type: contentType,
        slug,
        keypoints_count: keypointsCount ?? null,
        trigger,
        keypoint_interactions: keypointInteractionsRef.current,
        landed_on_keypoint: !!kpId,
        kp_id: kpId ?? null,
        // Placement, not origin. `surface` is the boot super-prop (= 'website'
        // here); passing it per-event would clobber it (§2c). Tag placement
        // via surface_detail instead.
        surface_detail: 'analysis_page',
      });
      sessionStorage.setItem(sessionKey, '1');

      // 2. CRE-793: also fire article_consumed on articles so the web
      // variant matches the iframe variant's event-name surface. Deduped
      // with its own sessionStorage key.
      if (contentType === 'article') {
        const articleKey = ARTICLE_SESSION_KEY(slug);
        if (!sessionStorage.getItem(articleKey)) {
          track('article_consumed', {
            article_id: slug,
            read_pct: readPct,
            surface_detail: 'analysis_page',
          });
          sessionStorage.setItem(articleKey, '1');
        }
      }

      if (scrollLegEnabled)
        scrollTarget.removeEventListener('scroll', onScroll);
      window.removeEventListener(KEYPOINT_INTERACTED_EVENT, onKeypoint);
    };

    const onScroll = () => {
      if (scrolledPastRef.current) return;
      let scrollTop: number;
      let maxScroll: number;
      if (scrollEl) {
        scrollTop = scrollEl.scrollTop;
        maxScroll = scrollEl.scrollHeight - scrollEl.clientHeight;
      } else {
        const doc = document.documentElement;
        scrollTop = window.scrollY || doc.scrollTop;
        maxScroll = doc.scrollHeight - window.innerHeight;
      }
      if (maxScroll <= 0) return;
      const pct = scrollTop / maxScroll;
      lastScrollPctRef.current = pct;
      if (pct >= SCROLL_THRESHOLD_PCT) {
        scrolledPastRef.current = true;
        fire('scroll_30pct');
      }
    };

    const ownPath = `/videos/youtube/${slug}`;
    const onKeypoint = () => {
      if (onlyOnOwnPath) {
        const path = window.location.pathname;
        if (path !== ownPath && !path.startsWith(`${ownPath}/`)) return;
      }
      keypointInteractionsRef.current += 1;
      if (keypointInteractionsRef.current >= KEYPOINT_INTERACTIONS_THRESHOLD) {
        fire('keypoint_2plus');
      }
    };

    // Listen on the real scroll surface (container) with a window fallback.
    // Scroll leg is article-only (see scrollLegEnabled above).
    const scrollTarget: HTMLElement | Window = scrollEl ?? window;
    if (scrollLegEnabled) {
      scrollTarget.addEventListener('scroll', onScroll, { passive: true });
    }
    // Custom event bus for keypoint interactions. Any keypoint UI
    // component calls `dispatchKeypointInteracted()` (exported above)
    // when the user interacts. The tracker counts + fires at threshold.
    window.addEventListener(KEYPOINT_INTERACTED_EVENT, onKeypoint);

    // Run an initial check in case the user is already past threshold
    // (reload at scroll position, anchor link, ?kp= scroll-to).
    if (scrollLegEnabled) onScroll();

    return () => {
      if (scrollLegEnabled) {
        scrollTarget.removeEventListener('scroll', onScroll);
      }
      window.removeEventListener(KEYPOINT_INTERACTED_EVENT, onKeypoint);
    };
  }, [contentType, slug, keypointsCount, kpId, onlyOnOwnPath, scrollContainer]);

  return null;
};
